import { useMemo, type ReactNode } from 'react';
import { CheckmarkFilled } from '@carbon/react/icons';
import { format0 } from 'utils/number';
import type { DocumentRow, CardInterpretation, CardWorkspace } from '../types';
import { AfeExpand } from './expand/afe-expand';
import { DoExpand } from './expand/do-expand';
import { RcExpand } from './expand/rc-expand';
import { TransactionsExpand } from './expand/transactions-expand';
import { PartiesTable } from './expand/parties-table';
import { PartiesSourceSection } from './expand/parties-source-section';
import { CurativeRequirements } from './expand/curative-requirements';
import { WorkspaceCard } from './expand/workspace-card';
import { ApplicableAreasSection, ExpandAccordion } from './expand/common';
import { DocumentCardNote } from './document-card-note';
import type { ExpandAllBroadcast } from './utils';
import styles from './document-card.module.scss';

interface SectionProps {
  document: DocumentRow;
  areas: string[];
  note?: string;
  // Opens every sub-section: expand/collapse-all or the prev/next open pulse.
  sectionSignal?: ExpandAllBroadcast;
  // Opens the Parties section; also fired by a parties "Show more".
  partiesSignal?: ExpandAllBroadcast;
}

/**
 * The type-specific (AFE/DO/RC) expand: parties by source, areas, notes and
 * the lazy-loaded type content, in each type's mock order.
 * @param {SectionProps} props The expand.
 * @return {JSX.Element | null} The expand.
 */
function TypedExpand({
  document,
  areas,
  note,
  sectionSignal,
  partiesSignal,
}: SectionProps) {
  // Notes as a collapsible band, a sibling above the type-specific content
  // (collapsed by default, mock parity).
  const notes = note ? (
    <ExpandAccordion title="Notes" openSignal={sectionSignal}>
      <DocumentCardNote note={note} />
    </ExpandAccordion>
  ) : null;
  const parties = (
    <PartiesSourceSection
      sources={document.partySources}
      openSignal={partiesSignal}
    />
  );
  const areasSection = (
    <ApplicableAreasSection areas={areas} openSignal={sectionSignal} />
  );
  if (document.type === 'Document::Afe') {
    return (
      <>
        {parties}
        {areasSection}
        {notes}
        <AfeExpand documentId={document.id} openSignal={sectionSignal} />
      </>
    );
  }
  if (document.type === 'Document::DivisionOrder') {
    return (
      <>
        {notes}
        {parties}
        {areasSection}
        <DoExpand documentId={document.id} openSignal={sectionSignal} />
      </>
    );
  }
  if (document.type === 'Document::RoyaltyCheck') {
    return (
      <>
        {parties}
        {areasSection}
        {notes}
        <RcExpand
          salesforceRoyaltyCheckId={document.salesforceRoyaltyCheckId}
          checkTotal={document.checkTotal}
          openSignal={sectionSignal}
        />
      </>
    );
  }
  return null;
}

/**
 * The Title Workspace section: one collapsible card per calculated workspace,
 * the first open by default.
 * @param {object} props The section.
 * @param {CardWorkspace[]} props.workspaces The document's workspaces.
 * @param {ExpandAllBroadcast} [props.openSignal] The section open signal.
 * @return {JSX.Element} The section.
 */
function WorkspacesSection({
  workspaces,
  openSignal,
}: {
  workspaces: CardWorkspace[];
  openSignal?: ExpandAllBroadcast;
}) {
  const ownershipRefs = useMemo(
    () => new Map<number, HTMLElement | null | undefined>(),
    []
  );
  return (
    <ExpandAccordion
      title={
        workspaces.length > 0
          ? `Title Workspace (${workspaces.length})`
          : 'Title Workspace'
      }
      openSignal={openSignal}
    >
      {workspaces.length > 0 ? (
        <div className={styles.workspaceGroup}>
          {workspaces.map((workspace, i) => (
            <ExpandAccordion
              key={workspace.id}
              // The name links to the workspace (new tab); the section header
              // already labels it, so the "Title Workspace :" prefix is dropped
              // (mock parity).
              title={
                <a
                  href={workspace.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.workspaceLink}
                  onClick={(e) => e.stopPropagation()}
                >
                  {workspace.name}
                </a>
              }
              // First workspace open by default, the rest collapsed.
              defaultOpen={i === 0}
              openSignal={openSignal}
            >
              <WorkspaceCard workspace={workspace} refsMap={ownershipRefs} />
            </ExpandAccordion>
          ))}
        </div>
      ) : (
        <p className={styles.muted}>
          No workspace calculation available for this document.
        </p>
      )}
    </ExpandAccordion>
  );
}

interface InterpretationExpandProps extends SectionProps {
  interpretations: CardInterpretation[];
  count: number;
  onOpenInterp: (interp: CardInterpretation) => void;
}

/**
 * The interpretation-driven expand for courthouse-style documents: parties,
 * areas, transactions, curative requirements, notes and title workspaces.
 * @param {InterpretationExpandProps} props The expand.
 * @return {JSX.Element} The expand.
 */
function InterpretationExpand({
  document,
  areas,
  note,
  sectionSignal,
  partiesSignal,
  interpretations,
  count,
  onOpenInterp,
}: InterpretationExpandProps) {
  // Open curative requirements across ALL interpretations — the collapsed-card
  // flag is any-interpretation, so scoping the accordion to only the latest one
  // hid it for docs whose curative sits on an earlier interpretation.
  const openCuratives = interpretations.flatMap(
    (interp) => interp.curativeRequirements ?? []
  );
  const curativeReviewed = interpretations.some(
    (interp) => interp.reviewAttributes?.curativeRequirementsReviewed
  );
  /**
   * Opens the tear sheet for a transaction row, resolving the interpretation
   * by id from the card's own list.
   * @param {number | string} interpId The interpretation's id.
   */
  const openInterpById = (interpId: number | string) => {
    const interp = interpretations.find(
      (i) => String(i.id) === String(interpId)
    );
    if (interp) onOpenInterp(interp);
  };
  let curativeTitle: ReactNode = 'Curative Requirements';
  if (curativeReviewed) {
    curativeTitle = (
      <>
        Curative Requirements
        <CheckmarkFilled
          className={styles.reviewComplete}
          aria-label="Reviewed"
        />
      </>
    );
  }

  return (
    <>
      {document.partiesBreakdown && (
        <ExpandAccordion title="Parties" openSignal={partiesSignal}>
          <PartiesTable breakdown={document.partiesBreakdown} />
        </ExpandAccordion>
      )}

      <ApplicableAreasSection areas={areas} openSignal={sectionSignal} />

      {count > 0 && (
        <div>
          <ExpandAccordion
            title={`Transaction Table (${format0(
              document.transactionsCount ?? 0
            )})`}
            openSignal={sectionSignal}
          >
            <TransactionsExpand
              documentId={document.id}
              onOpenInterp={openInterpById}
            />
          </ExpandAccordion>
        </div>
      )}

      {openCuratives.length > 0 && (
        <ExpandAccordion title={curativeTitle} openSignal={sectionSignal}>
          <CurativeRequirements requirements={openCuratives} />
        </ExpandAccordion>
      )}

      {note && (
        <ExpandAccordion title="Notes" openSignal={sectionSignal}>
          <DocumentCardNote note={note} />
        </ExpandAccordion>
      )}

      <WorkspacesSection
        workspaces={document.workspaces ?? []}
        openSignal={sectionSignal}
      />
    </>
  );
}

interface DocumentCardExpandProps extends InterpretationExpandProps {
  isTypedExpand: boolean;
}

/**
 * The expanded card body: the typed (AFE/DO/RC) expand or the
 * interpretation-driven one.
 * @param {DocumentCardExpandProps} props The body.
 * @return {JSX.Element} The body.
 */
export function DocumentCardExpand({
  isTypedExpand,
  ...props
}: DocumentCardExpandProps) {
  return isTypedExpand ? (
    <TypedExpand {...props} />
  ) : (
    <InterpretationExpand {...props} />
  );
}
